import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import routes from '@/shared/constants/routesPath';
import useUserStore from '@/shared/store/user';

import { GetUserData, LoginUser } from '@/services/auth';

import useAppleLogin from './useAppleLogin';
import useFacebookLogin from './useFacebookLogin';
import useGoogleLogin from './useGoogleLogin';

const useLogin = () => {
  const navigate = useNavigate();
  const { saveUser } = useUserStore();
  const [isLoading, setIsLoading] = useState(false);
  const [providersError, setProvidersError] = useState(null);

  // Social providers hooks
  const [googleUser, googleError] = useGoogleLogin();
  const [signInWithFacebook, facebookUser, facebookLoading, facebookError] =
    useFacebookLogin();
  const [signInWithApple, appleUser, appleLoading, appleError] =
    useAppleLogin();

  const { mutate: Login, isError: isLoginError } = useMutation({
    mutationFn: LoginUser,
    onSuccess: ({ data }) => {
      // get the user data
      return new Promise((resolve, reject) => {
        GetUserData(data.id)
          .then((res) => {
            // save the entire user data to the Store
            saveUser({ ...res.data, id: data.id });

            // then navigate to the Home page
            navigate(routes.HOME);
            resolve();
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            setIsLoading(false);
          });
      });
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Use a valid email address')
        .required('Type your email address'),
      password: Yup.string().required('Type your password'),
    }),
    onSubmit: (values) => {
      // set the loading state to true
      setIsLoading(true);
      // clean error state
      setProvidersError(false);
      // call the login mutation
      Login({
        ...values,
        provider: 'email',
      });
    },
  });

  // useEffect to trigger the login mutation when the user uses the Google
  // Facebook or Apple login providers
  useEffect(() => {
    setProvidersError(false); // reset error state
    if (googleUser) {
      return Login({
        token: googleUser.credential,
        provider: 'google',
      });
    }
    if (facebookUser) {
      return Login({
        token: facebookUser.accessToken,
        provider: 'facebook',
      });
    }
    if (appleUser) {
      return Login({
        token: appleUser.id_token,
        provider: 'apple',
      });
    }
  }, [googleUser, facebookUser, appleUser, Login]);

  // to handle errors from login providers
  useEffect(() => {
    if (googleError || facebookError || appleError) {
      setProvidersError(true);
    }
  }, [googleError, facebookError, appleError]);

  return {
    formik,
    isLoading: isLoading || facebookLoading || appleLoading,
    isLoginError,
    providersError,
    signInWithFacebook,
    signInWithApple,
  };
};

export default useLogin;
